function isBlankInfoValue(v) {
    if (v === null || v === undefined) return true;
    if (typeof v === 'string') return String(v).trim() === '';
    return false;
}

function isTruthyInfoValue(v) {
    if (typeof v === 'boolean') return v === true;
    const s = String(v ?? '').trim().toLowerCase();
    if (!s) return false;
    return ['1', 'true', 'yes', 'y', 'on'].includes(s);
}

function isFalsyInfoValue(v) {
    if (typeof v === 'boolean') return v === false;
    const s = String(v ?? '').trim().toLowerCase();
    if (!s) return false;
    return ['0', 'false', 'no', 'n', 'off', 'na', 'n/a', 'not applicable'].includes(s);
}

function filterInfoRowFields({ tableName, row, fields }) {
    const tn = String(tableName || '');
    const r = row && typeof row === 'object' ? row : {};
    let out = Array.isArray(fields) ? [...fields] : [];

    function dropIfBlank(keys) {
        const ks = Array.isArray(keys) ? keys : [keys];
        const toDrop = new Set(
            ks
                .map((k) => String(k || '').trim())
                .filter(Boolean)
                .filter((k) => isBlankInfoValue(r[k]))
        );
        if (!toDrop.size) return;
        out = out.filter(([k]) => !toDrop.has(String(k)));
    }

    function dropAlways(keys) {
        const ks = Array.isArray(keys) ? keys : [keys];
        const toDrop = new Set(ks.map((k) => String(k || '').trim()).filter(Boolean));
        if (!toDrop.size) return;
        out = out.filter(([k]) => !toDrop.has(String(k)));
    }

    // Rule 1: Surveys – knotweed remediation is only applicable when knotweed is present.
    if (tn === 'ini_surveys') {
        const knotweedPresent = r.japanese_knotweed_present;
        const remediation = r.knotweed_remediation_status;

        if ((isFalsyInfoValue(knotweedPresent) || knotweedPresent === null) && isBlankInfoValue(remediation)) {
            dropIfBlank('knotweed_remediation_status');
        }

        // Further surveys list only applies when further surveys are required.
        const further = r.further_surveys_required;
        if ((isFalsyInfoValue(further) || further === null) && isBlankInfoValue(r.further_surveys_list)) {
            dropIfBlank('further_surveys_list');
        }

        // Utility upgrades description only applies when upgrades are required.
        const upgrades = r.utility_upgrades_required;
        if ((isFalsyInfoValue(upgrades) || upgrades === null) && isBlankInfoValue(r.utility_upgrades_description)) {
            dropIfBlank('utility_upgrades_description');
        }

        // Utilities capacity summary only applies when searches/capacity checks have been completed.
        const utilSearches = r.utilities_searches_completed;
        if ((isFalsyInfoValue(utilSearches) || utilSearches === null) && isBlankInfoValue(r.utilities_capacity_summary)) {
            dropIfBlank('utilities_capacity_summary');
        }

        // Design drawings notes only applies when drawings have been provided.
        const drawings = r.design_drawings_provided;
        if ((isFalsyInfoValue(drawings) || drawings === null) && isBlankInfoValue(r.design_drawings_notes)) {
            dropIfBlank('design_drawings_notes');
        }

        // Specification notes only applies when a specification has been provided.
        const spec = r.specification_provided;
        if ((isFalsyInfoValue(spec) || spec === null) && isBlankInfoValue(r.specification_notes)) {
            dropIfBlank('specification_notes');
        }
    }

    // Rule 2: Planning applications – further planning status explanation is only applicable when
    // further planning notices exist AND a further decision has NOT been reached.
    if (tn === 'ini_planning_applications') {
        const planningSubmitted = r.planning_application_submitted;

        // These fields only appear when an application has been submitted.
        if (!isTruthyInfoValue(planningSubmitted)) {
            dropIfBlank([
                // Old/new schema variants
                'planning_authority',
                'approving_authority',
                'application_reference',
                'application_ref',
                'application_date',
                'decision_date',
                'proposal_description',
                'decision_notice_applicable',
                'planning_status_explanation',
                'decision'
            ]);
        } else {
            // Planning status explanation only applies when decision NOT reached.
            const decisionReached = r.decision_notice_applicable;
            if (isTruthyInfoValue(decisionReached) && isBlankInfoValue(r.planning_status_explanation)) {
                dropIfBlank('planning_status_explanation');
            }
            // Decision text only applies when decision reached.
            if (!isTruthyInfoValue(decisionReached) && isBlankInfoValue(r.decision)) {
                dropIfBlank('decision');
            }

            // Decision date only applies when decision reached.
            if (!isTruthyInfoValue(decisionReached)) {
                dropAlways('decision_date');
            } else if (isBlankInfoValue(r.decision_date)) {
                dropIfBlank('decision_date');
            }
        }

        const hasFurther = r.has_further_planning_notices;
        const furtherDecisionReached = r.further_decision_notice_applicable;
        const furtherStatus = r.further_planning_status_explanation;

        const furtherNotApplicable = isFalsyInfoValue(hasFurther) || hasFurther === null;

        // If no further planning notices, hide all follow-up fields for the further branch.
        if (furtherNotApplicable) {
            dropIfBlank([
                'further_planning_application_submitted',
                // Old/new schema variants
                'further_planning_authority',
                'further_approving_authority',
                'further_application_reference',
                'further_application_ref',
                'further_application_date',
                'further_notice_effect',
                'further_decision_notice_applicable',
                'further_planning_status_explanation',
                'further_decision'
            ]);
        } else {
            const furtherSubmitted = r.further_planning_application_submitted;

            // If a further application hasn't been submitted, hide its follow-ups.
            if (!isTruthyInfoValue(furtherSubmitted)) {
                dropIfBlank([
                    // Old/new schema variants
                    'further_planning_authority',
                    'further_approving_authority',
                    'further_application_reference',
                    'further_application_ref',
                    'further_application_date',
                    'further_notice_effect',
                    'further_decision_notice_applicable',
                    'further_planning_status_explanation',
                    'further_decision'
                ]);
            } else {
                // Explanation only applies when further decision NOT reached.
                const statusNotApplicable = isTruthyInfoValue(furtherDecisionReached);
                if (statusNotApplicable && isBlankInfoValue(furtherStatus)) {
                    dropIfBlank('further_planning_status_explanation');
                }

                // Further decision text only applies when further decision reached.
                if (!isTruthyInfoValue(furtherDecisionReached) && isBlankInfoValue(r.further_decision)) {
                    dropIfBlank('further_decision');
                }

                // Further decision date only applies when decision reached.
                if (!isTruthyInfoValue(furtherDecisionReached)) {
                    dropAlways('further_decision_date');
                } else if (isBlankInfoValue(r.further_decision_date)) {
                    dropIfBlank('further_decision_date');
                }
            }
        }

        // Listed Building Consent branch.
        const lbcRequired = r.listed_building_consent_required;
        const lbcNotApplicable = isFalsyInfoValue(lbcRequired) || lbcRequired === null;
        if (lbcNotApplicable) {
            dropIfBlank([
                'listed_building_application_submitted',
                // Old/new schema variants
                'listed_building_planning_authority',
                'listed_building_approving_authority',
                'listed_building_application_reference',
                'listed_building_application_ref',
                'listed_building_application_date',
                'listed_building_decision_notice_applicable',
                'listed_building_planning_status_explanation',
                'listed_building_decision'
            ]);
        } else {
            const lbcSubmitted = r.listed_building_application_submitted;
            if (!isTruthyInfoValue(lbcSubmitted)) {
                dropIfBlank([
                    // Old/new schema variants
                    'listed_building_planning_authority',
                    'listed_building_approving_authority',
                    'listed_building_application_reference',
                    'listed_building_application_ref',
                    'listed_building_application_date',
                    'listed_building_decision_notice_applicable',
                    'listed_building_planning_status_explanation',
                    'listed_building_decision'
                ]);
            } else {
                const lbcDecisionReached = r.listed_building_decision_notice_applicable;
                if (isTruthyInfoValue(lbcDecisionReached) && isBlankInfoValue(r.listed_building_planning_status_explanation)) {
                    dropIfBlank('listed_building_planning_status_explanation');
                }
                if (!isTruthyInfoValue(lbcDecisionReached) && isBlankInfoValue(r.listed_building_decision)) {
                    dropIfBlank('listed_building_decision');
                }

                // Listed Building decision date only applies when decision reached.
                if (!isTruthyInfoValue(lbcDecisionReached)) {
                    dropAlways('listed_building_decision_date');
                } else if (isBlankInfoValue(r.listed_building_decision_date)) {
                    dropIfBlank('listed_building_decision_date');
                }
            }
        }
    }

    // Rule 3: Location & Site Information – hide conditional follow-ups when not applicable.
    if (tn === 'ini_section_3_location') {
        // Borrower GIA is displayed in Section 7 (Cost) as "Developer GIA (m²)".
        // Avoid duplicate display under Location & Site.
        // Also hide AI-formulated gap-filler fields that are not in the questionnaire.
        dropAlways([
            'borrower_gia_m2',
            'location_description',
            'surrounding_area',
            'access_description',
            'site_area_sq_m'
        ]);

        const devType = String(r.development_type || '').trim();
        if (devType !== 'Other') dropIfBlank('development_type_other');

        if (devType !== 'Residential') {
            dropIfBlank(['residential_type', 'house_type', 'house_type_other']);
        } else {
            const resType = String(r.residential_type || '').trim();
            if (resType !== 'Houses') {
                dropIfBlank(['house_type', 'house_type_other']);
            } else {
                const houseType = String(r.house_type || '').trim();
                if (houseType !== 'Other') dropIfBlank('house_type_other');
            }
        }

        const progressed = r.works_progressed;
        if ((isFalsyInfoValue(progressed) || progressed === null) && isBlankInfoValue(r.works_progressed_description)) {
            dropIfBlank('works_progressed_description');
        }

        const occ = String(r.site_occupation_status || '').trim();
        if (occ !== 'Occupied' && occ !== 'Part-occupied') {
            dropIfBlank('site_occupation_details');
        }

        const access = String(r.delivery_access_constraints || '').trim();
        if (access !== 'Yes') {
            dropIfBlank('delivery_access_constraints_details');
        }
    }

    // Rule 4: Planning conditions – counts/details only apply when conditions are relevant.
    if (tn === 'ini_section_4_planning_conditions') {
        const relevant = r.planning_conditions_relevant;

        // Optional fields should not clutter the Information Summary when blank.
        dropIfBlank(['total_conditions_count', 'key_conditions_summary']);

        if (isFalsyInfoValue(relevant) || relevant === null) {
            dropIfBlank([
                'pre_commencement_conditions_count',
                'total_conditions_count',
                'key_conditions_summary',
                'discharge_progress',
                'conditions_discharged_status',
                'conditions_application_status'
            ]);
        }
    }

    // Rule 5: Statutory elements – hide conditional follow-ups when not applicable.
    if (tn === 'ini_section_4_statutory') {
        // Optional fields should not clutter the Information Summary when blank.
        dropIfBlank([
            'cil_payment_status',
            'cil_amount',
            'cil_paid_state',
            'health_safety_overview',
            'asbestos_status',
            'construction_phase_plan_status',
            'site_constraints_safety',
            'warranty_status'
        ]);

        const bcAppointed = r.building_control_officer_appointed;
        if (!isTruthyInfoValue(bcAppointed)) {
            dropIfBlank(['building_control_officer_name', 'initial_notice_issued', 'initial_notice_status']);
        }
        if (!isFalsyInfoValue(bcAppointed)) {
            dropIfBlank('building_control_officer_not_appointed_reason');
        }

        const initialNoticeIssued = r.initial_notice_issued;
        if (isTruthyInfoValue(initialNoticeIssued) && isBlankInfoValue(r.initial_notice_status)) {
            dropIfBlank('initial_notice_status');
        }

        const warrantyType = String(r.warranty_type || '').trim();
        if (warrantyType && warrantyType !== 'N/A') {
            dropIfBlank('warranty_na_reason');
        } else {
            dropIfBlank(['warranty_document_provider', 'warranty_status']);
        }

        const cilRelevant = r.cil_relevant;
        if (!isTruthyInfoValue(cilRelevant)) {
            dropIfBlank(['cil_payment_status', 'cil_amount', 'cil_paid_state']);
        }

        const sectionAgreements = r.section_agreements_relevant;
        if (!isTruthyInfoValue(sectionAgreements)) {
            dropIfBlank(['section_106', 'section_104', 'section_38', 'section_278']);
        }

        const anyRelevant = [
            'cil_relevant',
            'section_agreements_relevant',
            'party_wall_awards_relevant',
            'easements_relevant',
            'archaeology_requirements_relevant',
            'conservation_area_relevant',
            'rights_of_light_relevant',
            'oversailing_agreement_relevant'
        ].some((k) => isTruthyInfoValue(r[k]));
        if (!anyRelevant) dropIfBlank('statutory_notes');
    }

    // Rule 6: Procurement – hide conditional follow-ups when not applicable.
    if (tn === 'ini_section_9_procurement') {
        const usingContractor = r.using_contractor;

        if (!isFalsyInfoValue(usingContractor)) dropIfBlank('constructed_by');
        if (!isTruthyInfoValue(usingContractor)) {
            dropIfBlank([
                'procurement_route',
                'procurement_route_other',
                'procurement_route_commentary',
                'tender_concluded',
                'chosen_contractor',
                'tender_status',
                'tender_type',
                'tender_type_other',
                'tenderers_invited_count',
                'tenderers_returned_count',
                'tender_return_date',
                'contract_sum',
                'contract_start_on_site',
                'contract_practical_completion',
                'contract_duration_months',
                'retention_percent',
                'lad_rate',
                'performance_bond_status',
                'pcg_status',
                'dev_project_insurance_table',
                'contractor_insurance_table',
                'consultants_pi_insurance_table',
                'subcontractor_insurance_table',
                'dev_project_insurance_summary',
                'dev_project_insurance_expiry',
                'contractor_public_liability',
                'contractor_employers_liability',
                'contract_works_insurance',
                'contractor_insurance_expiry',
                'consultants_pi_summary',
                'consultants_pi_expiry'
            ]);
        } else {
            const route = String(r.procurement_route || '').trim();
            if (route !== 'Other') dropIfBlank('procurement_route_other');

            const tenderConcluded = r.tender_concluded;
            if (!isTruthyInfoValue(tenderConcluded)) dropIfBlank('chosen_contractor');
            if (!isFalsyInfoValue(tenderConcluded)) dropIfBlank('tender_status');

            const tenderType = String(r.tender_type || '').trim();
            if (tenderType !== 'Other') dropIfBlank('tender_type_other');
        }

        const jctUsed = r.jct_used;
        if (!isTruthyInfoValue(jctUsed)) {
            dropIfBlank(['jct_form', 'jct_other_description', 'contract_signed', 'contract_process_status']);
        } else {
            const jctForm = String(r.jct_form || '').trim();
            if (jctForm !== 'Other') dropIfBlank('jct_other_description');

            const contractSigned = r.contract_signed;
            if (!isFalsyInfoValue(contractSigned)) dropIfBlank('contract_process_status');
        }
    }

    return out;
}

export {
    filterInfoRowFields,
    isBlankInfoValue,
    isTruthyInfoValue,
    isFalsyInfoValue
};
